import React, { useState } from "react";
import { Lock, Loader2, Mail, Eye, EyeOff } from "lucide-react";
import { useAuth } from "./auth";

export default function Login({ subtitle = "Sign in to continue" }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (ex) {
      setErr(ex.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="flex min-h-screen items-center justify-center bg-heda-cream px-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl border border-heda-line bg-white p-7 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.15)] sm:p-8"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/heda-logo.png" alt="HEDA Plywoods" className="h-20 w-auto" />
          <h1 className="mt-4 font-display text-2xl font-bold text-heda-ink">Hedaply AI</h1>
          <p className="mt-1 text-[13px] text-heda-mute">{subtitle}</p>
        </div>

        <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
          Email
        </label>
        <div className="relative mb-4">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
          <input
            data-testid="login-email"
            type="email"
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-heda-line bg-heda-cream/50 py-3 pl-9 pr-3 text-[15px] outline-none transition focus:border-heda-gold"
            placeholder="you@hedaplyai.com"
            required
          />
        </div>

        <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
          Password
        </label>
        <div className="relative">
          <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
          <input
            data-testid="login-password"
            type={showPw ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-heda-line bg-heda-cream/50 py-3 pl-9 pr-10 text-[15px] outline-none transition focus:border-heda-gold"
            placeholder="••••••••"
            required
          />
          <button
            type="button"
            data-testid="login-password-toggle"
            aria-label={showPw ? "Hide password" : "Show password"}
            onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-heda-mute"
          >
            {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>

        {err && (
          <div
            data-testid="login-error"
            className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
          >
            {err}
          </div>
        )}

        <button
          data-testid="login-submit"
          disabled={loading || !email || !password}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-heda-ink px-4 py-3 text-sm font-semibold text-white transition hover:bg-black disabled:opacity-40"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
          {loading ? "Signing in…" : "Sign In"}
        </button>

        <p className="mt-5 text-center text-[11.5px] text-heda-mute">
          HEDA Plywoods · Authorised users only
        </p>
      </form>
    </div>
  );
}
